import { Icon } from '@fluentui/react';
import { cloneElement, forwardRef, HTMLAttributes, ReactElement, ReactNode, Ref } from 'react';

import { IComponent } from '../../models/components.models';
import { useClassNames } from '../../utils/hooks';
import { basicCardStylesDefault, IBasicCardClassNames, IBasicCardStyles } from './BasicCard.styles';

export interface IBasicCardProps<T extends { [key: string]: any }>
  extends IComponent<IBasicCardStyles>,
    Omit<HTMLAttributes<HTMLElement>, 'onClick'> {
  data: T;
  idField: keyof T;
  titleField: keyof T;
  descriptionField?: keyof T;
  imagePathField?: keyof T;
  countField?: keyof T;
  iconField?: keyof T;
  selected?: boolean;
  height?: string;
  onRenderDescription?: (data: T) => ReactNode;
  onClick?: (data: T, e: React.MouseEvent<HTMLButtonElement>) => void;
}

export const BasicCard = forwardRef(
  <T extends { [key: string]: any }>(props: IBasicCardProps<T>, ref: Ref<HTMLElement>) => {
    const {
      className,
      styles,
      data,
      idField = 'id' as keyof T,
      titleField = 'title' as keyof T,
      descriptionField,
      imagePathField,
      countField,
      iconField,
      selected,
      height = '116px',
      onRenderDescription,
      onClick,
      ...elementProps
    } = props;
    const { style = {} } = props;
    const classNames = useClassNames<Partial<IBasicCardStyles>, IBasicCardClassNames>(
      'BasicCard',
      basicCardStylesDefault,
      styles,
      className,
      selected && 'selected',
      onClick && 'clickable',
      iconField && data[iconField] && 'has-icon'
    );
    return (
      <>
        {cloneElement(
          onClick ? <button onClick={(event) => onClick(data, event)} /> : <div />,
          {
            ...elementProps,
            ref,
            key: `${data[idField]}`,
            className: classNames.root,
            style: { ...style, height },
            'data-test': props['data-test'] || 'basic-card',
          },
          <>
            {imagePathField && (
              <div
                className={classNames.thumbnail}
                style={{
                  backgroundImage: data[imagePathField] ? `url('${data[imagePathField]}')` : `url('logo512.png')`,
                }}
              ></div>
            )}
            <div className={classNames.content}>
              <div className={classNames.title}>
                <div className={classNames.titleText} title={`${data[titleField]}`}>{`${data[titleField]}`}</div>
              </div>
              <div className={classNames.contentBody}>
                {(descriptionField || onRenderDescription) && (
                  <div className={classNames.description}>
                    {descriptionField
                      ? `${data[descriptionField]}`
                      : onRenderDescription
                      ? onRenderDescription(data)
                      : ''}
                  </div>
                )}
                {countField && <div className={classNames.count}>{`${data[countField]}`}</div>}
              </div>
              {iconField && (
                <div className={classNames.icon}>
                  <Icon iconName={data[iconField]} />
                </div>
              )}
            </div>
          </>
        )}
      </>
    );
  }
) as <T extends { [key: string]: any }>(props: IBasicCardProps<T> & { ref?: Ref<HTMLElement> }) => ReactElement;
